import type { FC, ReactNode } from 'react'

import { useRouter } from 'next/router'
import {
    createContext,
    useCallback,
    useContext,
    useMemo,
    useRef
} from 'react'

import { Logo } from '@/components/generals/logo'
import { useSmoothScroll } from '@/components/generals/smooth-scroll'
import { beginPageTransition, endPageTransition } from '@/lib/transition'

import {
    gsap,
    REDUCED_MOTION,
    ScrollTrigger
} from '@/lib/gsap'

import { pageLabelFor } from '@/constants/site'

interface PageTransitionValue {
    navigate: (href: string) => void
}

interface PageTransitionProviderProps {
    children: ReactNode
}

const PageTransitionContext = createContext<PageTransitionValue>({
    navigate: () => undefined
})

const nextFrame = (): Promise<void> => new Promise((resolve) => {
    window.requestAnimationFrame(() => window.requestAnimationFrame(() => resolve()))
})

/**
 * Owns route changes: a curtain closes, the page swaps underneath it,
 * the scroll resets, then the curtain lifts onto the new page.
 */
export const PageTransitionProvider: FC<PageTransitionProviderProps> = ({ children }): ReactNode => {
    const router = useRouter()
    const { scrollTo, resize } = useSmoothScroll()
    const curtainRef = useRef<HTMLDivElement>(null)
    const labelRef = useRef<HTMLSpanElement>(null)
    const busy = useRef(false)

    const settleScroll = useCallback((hash: string): void => {
        resize()

        const target = hash ? document.querySelector<HTMLElement>(hash) : null

        if (target) scrollTo(target, { immediate: true, offset: -24 })
        else scrollTo(0, { immediate: true })

        ScrollTrigger.refresh()
    }, [resize, scrollTo])

    const navigate = useCallback((href: string): void => {
        const url = new URL(href, window.location.href)

        if (url.origin !== window.location.origin) {
            window.location.assign(url.href)
            return
        }

        if (url.pathname === window.location.pathname) {
            if (url.hash && document.querySelector(url.hash)) scrollTo(url.hash, { offset: -24 })
            else scrollTo(0, { duration: 1.8 })
            return
        }

        if (busy.current) return

        const destination = `${url.pathname}${url.search}${url.hash}`
        const curtain = curtainRef.current
        const label = labelRef.current

        if (!curtain || !label || window.matchMedia(REDUCED_MOTION).matches) {
            router.push(destination).then(() => settleScroll(url.hash)).catch(() => undefined)
            return
        }

        busy.current = true
        beginPageTransition()
        label.textContent = pageLabelFor(url.pathname)

        const close = gsap.timeline()

        close
            .set(curtain, { visibility: 'visible', pointerEvents: 'auto', clipPath: 'inset(100% 0% 0% 0%)' })
            .set(label, { yPercent: 110 })
            .to(curtain, { clipPath: 'inset(0% 0% 0% 0%)', duration: 0.8, ease: 'expo.inOut' })
            .to(label, { yPercent: 0, duration: 0.8, ease: 'expo.out' }, 0.4)

        close.then(async () => {
            try {
                await router.push(destination, undefined, { scroll: false })
            } catch {
                // A cancelled or failed route change still has to lift the curtain.
            }

            await nextFrame()
            settleScroll(url.hash)
            await nextFrame()

            endPageTransition()

            gsap.timeline({
                onComplete: () => {
                    gsap.set(curtain, { visibility: 'hidden', pointerEvents: 'none' })
                    busy.current = false
                }
            })
                .to(label, { yPercent: -110, duration: 0.5, ease: 'expo.in' })
                .to(curtain, { clipPath: 'inset(0% 0% 100% 0%)', duration: 1, ease: 'expo.inOut' }, 0.15)
        })
    }, [router, scrollTo, settleScroll])

    const value = useMemo(() => ({ navigate }), [navigate])

    return (
        <PageTransitionContext.Provider value={value}>
            {children}

            <div
                ref={curtainRef}
                aria-hidden='true'
                className='pointer-events-none invisible fixed inset-0 z-[90] flex flex-col items-center justify-center gap-6 bg-pine text-bone'
                style={{ clipPath: 'inset(100% 0% 0% 0%)' }}
            >
                <Logo showMark className='text-bone/70' />

                <span className='block overflow-hidden px-6 pb-[0.1em]'>
                    <span ref={labelRef} className='type-wide block text-center text-[clamp(2rem,6vw,5.5rem)] font-medium uppercase leading-none tracking-[-0.02em]'>
                        Morrow
                    </span>
                </span>
            </div>
        </PageTransitionContext.Provider>
    )
}

export const usePageTransition = (): PageTransitionValue => useContext(PageTransitionContext)
